import {
  Col,
  ENotificationType,
  Row,
  TRtkErrorType,
  useShowToastNotification,
  useTranslation,
  filterTitleOption,
} from '@packages/utils';
import { EInvitationStatus } from 'data/types/elcs.types';
import EditableSelect from 'components/Base/EditableFields/EditableSelect';
import { InplaceEditLabels } from 'components/Base/EditableFields/components/InplaceEditLabel';
import { useEditSecondPMMutation } from 'redux/apiSlice/elcs.slice';
import { userGrowApi } from 'redux/apiSlice/userGrow.slice';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { EIconNames } from 'components/Base/Icon';
import PendingIconTooltip from 'pages/private/ELCs/components/PendingIconTooltip';
import ElcItemPopover from 'pages/private/ELCs/components/ElcPopover';
import useUserOptions from 'hooks/useUserOptions';
import { TSecondPMProps } from './SecondPM.types';
import { avatarColorMapper } from '../../../../ELCs.data';
import ConfirmModalBody from '../ConfirmModalBody';

const SecondPM = ({ data, elcId, isEditAllowed }: TSecondPMProps) => {
  const dispatch = useAppDispatch();
  const usersData = useAppSelector(state => state.userSlice);
  const { user } = usersData;
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.elcs.list.elc-item.pm',
  });
  const { showToastNotification } = useShowToastNotification();

  const pmOptions = useUserOptions();

  const [
    editSecondPM,
    { isLoading: secondPMEditIsLoading, isError: secondPMEditIsError },
  ] = useEditSecondPMMutation();

  const onFirstPMEdit = async (value: string) => {
    try {
      await editSecondPM({
        elcId,
        userId: value,
      }).unwrap();
      if (value === user.userId || data?.userId === user.userId) {
        dispatch(userGrowApi.util.invalidateTags(['user']));
      }
      showToastNotification({
        message: t('SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (e) {
      showToastNotification({
        message: (e as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  };

  return (
    <Row align="middle" wrap={false}>
      <Col flex="auto">
        <Row align="middle">
          <Col>
            <InplaceEditLabels label={t('LABEL')} icon={EIconNames.PROFILE} />
          </Col>
          <Col flex="auto">
            <EditableSelect
              {...(data && {
                title: {
                  label: data.fullName,
                  value: data.userId,
                },
              })}
              fitContent={false}
              readonly={!isEditAllowed}
              canRemove={data?.allowedUpdateWithNull}
              onEdit={onFirstPMEdit}
              options={pmOptions}
              emptyText={t('EMPTY_TEXT')}
              placeholder={t('PLACEHOLDER')}
              {...(data && {
                tooltip: (
                  <ElcItemPopover
                    fullName={data.fullName}
                    email={data.email}
                    phone={data.phone}
                  />
                ),
              })}
              selectProps={{
                filterOption: filterTitleOption,
                showSearch: true,
              }}
              avatarColor={
                data?.invitationStatus === EInvitationStatus.PENDING
                  ? 'grey'
                  : avatarColorMapper.firstPM
              }
              isError={secondPMEditIsError}
              isLoading={secondPMEditIsLoading}
              data-cy={data ? 'pm2_name' : 'add_pm2_btn'}
              {...(data && {
                modalBody: <ConfirmModalBody />,
              })}
            />
          </Col>
        </Row>
      </Col>
      {data?.invitationStatus === EInvitationStatus.PENDING && (
        <PendingIconTooltip />
      )}
    </Row>
  );
};

export default SecondPM;
